import React, { useState, useEffect } from "react";
import { Plus, Settings2, GripVertical, Search, Filter } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function CRMPipeline() {
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // Dialog state
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [editStages, setEditStages] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Migrate old statuses to pipeline stages first time
      await api.post("/crm/migrate");
      
      const [pipeRes, boardRes] = await Promise.all([
        api.get("/crm/pipeline"),
        api.get("/crm/board")
      ]);
      setStages(pipeRes.data.stages || []);
      setEditStages(pipeRes.data.stages || []);
      setLeads(boardRes.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };

  // HTML5 Drag and Drop Handlers
  const handleDragStart = (e, leadId) => {
    e.dataTransfer.setData("leadId", leadId);
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const handleDrop = async (e, targetStageId) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("leadId");
    if (!leadId) return;

    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.pipeline_stage === targetStageId) return;

    // Optimistic UI update
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, pipeline_stage: targetStageId } : l));

    try {
      await api.patch(`/crm/leads/${leadId}/stage`, { pipeline_stage: targetStageId });
      toast.success("Lead moved");
    } catch (error) {
      console.error(error);
      toast.error("Failed to move lead");
      fetchData(); // Revert on failure
    }
  };

  // Stage Customization Handlers
  const saveStages = async () => {
    try {
      await api.put("/crm/pipeline", { stages: editStages });
      setStages(editStages);
      setIsCustomizeOpen(false);
      toast.success("Pipeline stages saved");
    } catch (e) {
      console.error(e);
      toast.error("Failed to save stages");
    }
  };

  const addStage = () => {
    setEditStages([...editStages, { id: `s_${Date.now()}`, name: "New Stage", order: editStages.length, color: "#9A9AA3" }]);
  };

  const removeStage = (stageId) => {
    if (leads.some(l => l.pipeline_stage === stageId)) {
      toast.error("Cannot delete a stage that contains leads. Move them first.");
      return;
    }
    setEditStages(editStages.filter(s => s.id !== stageId));
  };

  const updateStage = (index, field, value) => {
    const updated = [...editStages];
    updated[index][field] = value;
    setEditStages(updated);
  };

  // Helpers
  const formatCurrency = (val) => {
    if (!val) return "$0";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  };

  const totalPipelineValue = leads.reduce((sum, l) => sum + (l.deal_value || 0), 0);
  
  const filteredLeads = leads.filter(l => 
    (l.name && l.name.toLowerCase().includes(searchTerm.toLowerCase())) || 
    (l.company && l.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) return <div className="p-10 text-center text-[#5C5C66]">Loading pipeline...</div>;

  return (
    <div className="flex flex-col h-[calc(100vh-1rem)] px-6 lg:px-8 py-6 max-w-[1600px] mx-auto overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 shrink-0">
        <div>
          <div className="label-mono mb-1">?" CRM Pipeline</div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Active Deals</h1>
          <div className="text-sm text-[#5C5C66] mt-1">
            Total Pipeline: <span className="font-mono font-medium text-[#0A0A0F]">{formatCurrency(totalPipelineValue)}</span> · {leads.length} active leads
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A9AA3]" />
            <Input 
              placeholder="Search deals..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64 rounded-lg bg-white" 
            />
          </div>
          
          <Dialog open={isCustomizeOpen} onOpenChange={setIsCustomizeOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-lg border-[#EAEAE3] bg-white text-[#5C5C66] hover:bg-[#F4F4EE]">
                <Settings2 className="h-4 w-4 mr-2" /> Customize Stages
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Customize Pipeline Stages</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                {editStages.map((stage, idx) => (
                  <div key={stage.id} className="flex items-center gap-2 p-2 border border-[#EAEAE3] rounded-lg bg-[#FAFAF7]">
                    <GripVertical className="h-4 w-4 text-[#9A9AA3] cursor-grab" />
                    <input 
                      type="color" 
                      value={stage.color} 
                      onChange={(e) => updateStage(idx, "color", e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer p-0 border-0"
                    />
                    <Input 
                      value={stage.name} 
                      onChange={(e) => updateStage(idx, "name", e.target.value)}
                      className="flex-1 bg-white h-8"
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeStage(stage.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2">
                      Remove
                    </Button>
                  </div>
                ))}
                <Button variant="outline" onClick={addStage} className="w-full mt-2 border-dashed">
                  <Plus className="h-4 w-4 mr-2" /> Add Stage
                </Button>
                <div className="flex justify-end mt-6">
                  <Button onClick={saveStages} className="bg-[#0A0A0F] text-white">Save Changes</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-4 h-full pb-4 items-start" style={{ width: 'max-content' }}>
          {stages.sort((a,b) => a.order - b.order).map(stage => {
            const stageLeads = filteredLeads.filter(l => l.pipeline_stage === stage.id);
            const stageTotal = stageLeads.reduce((sum, l) => sum + (l.deal_value || 0), 0);
            
            return (
              <div 
                key={stage.id} 
                className="w-[300px] flex flex-col h-full bg-white border border-[#EAEAE3] rounded-xl shrink-0 overflow-hidden"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Column Header */}
                <div className="p-3 border-b border-[#EAEAE3] bg-[#FAFAF7] shrink-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                      <span className="font-semibold text-sm text-[#0A0A0F]">{stage.name}</span>
                    </div>
                    <span className="text-xs font-mono font-medium text-[#5C5C66] bg-[#EAEAE3] px-2 py-0.5 rounded-full">
                      {stageLeads.length}
                    </span>
                  </div>
                  <div className="text-xs text-[#5C5C66] font-mono">{formatCurrency(stageTotal)}</div>
                </div>

                {/* Cards Container */}
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {stageLeads.map(lead => (
                    <div 
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead.id)}
                      onClick={() => navigate(`/app/leads/${lead.id}`)}
                      className="bg-white border border-[#EAEAE3] rounded-lg p-3 shadow-sm hover:shadow-md hover:border-[#002FA7]/30 transition-all cursor-pointer group"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium text-sm text-[#0A0A0F] truncate">{lead.name}</div>
                        <div className="text-xs font-mono font-bold text-[#002FA7] shrink-0 ml-2">{lead.score}</div>
                      </div>
                      <div className="text-xs text-[#5C5C66] truncate mb-3">
                        {lead.title && `${lead.title} · `}{lead.company}
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-medium text-[#0A0A0F]">
                          {formatCurrency(lead.deal_value)}
                        </span>
                        {lead.priority && (
                          <span className={`px-2 py-0.5 rounded-md text-[10px] uppercase font-mono tracking-wider
                            ${lead.priority === 'high' ? 'bg-red-50 text-red-600' : 
                              lead.priority === 'medium' ? 'bg-amber-50 text-amber-600' : 
                              'bg-gray-50 text-gray-600'}`}>
                            {lead.priority}
                          </span>
                        )}
                      </div>
                      {lead.tags && lead.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {lead.tags.map(tag => (
                            <span key={tag} className="text-[10px] bg-[#F4F4EE] text-[#5C5C66] px-1.5 py-0.5 rounded">#{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {stageLeads.length === 0 && (
                    <div className="h-20 flex items-center justify-center border-2 border-dashed border-[#EAEAE3] rounded-lg text-xs text-[#9A9AA3]">
                      Drop leads here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
